// Payment MODE (cash/card/account), distinct from the paid/unpaid STATE in tripPaymentStatus.js.
// passenger.account is Contact.Display = Code + '-' + CompanyName, hence "CASH-CASH".
// The server decides account-billing by GUID (paymentAPI.isAccountBilledTrip); that id is not
// shipped to the client, so we read the account CODE and let accountBilled override it.

import { resolveTripPaymentStatus, TRIP_PAYMENT_STATE } from './tripPaymentStatus';

export const TRIP_PAYMENT_MODE = {
  UNKNOWN: 'unknown',
  CASH: 'cash',
  CARD: 'card',
  ACCOUNT: 'account',
};

const MODE_LABELS = {
  [TRIP_PAYMENT_MODE.UNKNOWN]: '',
  [TRIP_PAYMENT_MODE.CASH]: 'Cash',
  [TRIP_PAYMENT_MODE.CARD]: 'Card',
  [TRIP_PAYMENT_MODE.ACCOUNT]: 'Account',
};

export const TRIP_PAYMENT_MODE_CHIP_CLASS = {
  [TRIP_PAYMENT_MODE.CASH]: 'bg-green-100 text-green-800 border-green-200',
  [TRIP_PAYMENT_MODE.CARD]: 'bg-sky-100 text-sky-800 border-sky-200',
  [TRIP_PAYMENT_MODE.ACCOUNT]: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  [TRIP_PAYMENT_MODE.UNKNOWN]: 'bg-slate-100 text-slate-500 border-slate-200',
};

// "CASH-CASH" -> "cash", "CC-Credit Card" -> "cc", "ACME-Acme Corp" -> "acme".
function accountCode(accountDisplay) {
  return String(accountDisplay || '').trim().split('-')[0].trim().toLowerCase();
}

// True when the reservation actually carries the server's payment block — an empty
// one resolves to 'fareNotFinal', not 'unknown', so callers need this distinction.
export function hasServerPaymentBlock(reservation) {
  if (!reservation || typeof reservation !== 'object') return false;
  return reservation.accountBilled !== undefined || reservation.paid !== undefined ||
    reservation.paymentStatus !== undefined || reservation.paymentRequired !== undefined;
}

const CASH_CODES = new Set(['cash']);
const CARD_CODES = new Set(['cc', 'card', 'credit', 'creditcard']);

// Account name with the code prefix dropped when it just repeats the name.
function accountName(accountDisplay) {
  const raw = String(accountDisplay || '').trim();
  if (!raw) return '';
  const dash = raw.indexOf('-');
  if (dash === -1) return raw;
  const code = raw.slice(0, dash).trim();
  const rest = raw.slice(dash + 1).trim();
  if (!rest) return code;
  if (rest.toLowerCase() === code.toLowerCase()) return code;
  return rest;
}

// How a SETTLED payment was actually tendered. AutoDispatch reports this as a display
// string on the payment block ('Cash' | 'Credit Card' | 'Wallet'), sourced from the
// Trip_Payment.PaymentTypeId it wrote at settlement.
function settledMode(reservation) {
  const raw = String(
    (reservation && (reservation.paymentMethod || reservation.PaymentMethod)) || ''
  ).trim().toLowerCase();
  if (!raw) return null;
  if (raw.indexOf('cash') > -1) return TRIP_PAYMENT_MODE.CASH;
  // 'Wallet' is prepaid card money, so it reads as Card at the door: either way the
  // driver collects nothing further by hand.
  if (raw.indexOf('credit') > -1 || raw.indexOf('card') > -1 || raw.indexOf('wallet') > -1) {
    return TRIP_PAYMENT_MODE.CARD;
  }
  return null;
}

export function resolveTripPaymentMode(trip) {
  const passenger = (trip && trip.passenger) || {};
  const reservation = (trip && trip.reservation) || null;

  const code = accountCode(passenger.account);
  const hasPaymentBlock = hasServerPaymentBlock(reservation);
  const status = resolveTripPaymentStatus(hasPaymentBlock ? reservation : null);
  const accountBilled = hasPaymentBlock
    ? status.state === TRIP_PAYMENT_STATE.ACCOUNT_BILLED
    : !!code && !CASH_CODES.has(code) && !CARD_CODES.has(code);

  if (accountBilled) {
    return build(TRIP_PAYMENT_MODE.ACCOUNT, accountName(passenger.account) ||
      String(passenger.billingType || '').trim(), true);
  }

  // Once money has actually been taken, HOW it was taken outranks the passenger's
  // billing arrangement. A CASH-account rider who paid by card at the door was showing
  // "Cash" forever, because the arrangement is all this used to read.
  //
  // Deliberately below the accountBilled branch: an account trip stays "Account"
  // whatever the settlement says, because that chip tells the driver not to collect at
  // the door — an arrangement fact, not a tender fact.
  if (status.isPaid) {
    const tendered = settledMode(reservation);
    if (tendered) return build(tendered, '', false);
  }

  if (CASH_CODES.has(code)) return build(TRIP_PAYMENT_MODE.CASH, '', false);
  if (CARD_CODES.has(code)) return build(TRIP_PAYMENT_MODE.CARD, '', false);

  // No usable code (flag trips can arrive before the account is stamped).
  const billing = String(passenger.billingType || '').trim().toLowerCase();
  if (billing.indexOf('cash') > -1) return build(TRIP_PAYMENT_MODE.CASH, '', false);
  if (billing.indexOf('credit') > -1 || billing.indexOf('card') > -1) {
    return build(TRIP_PAYMENT_MODE.CARD, '', false);
  }

  return build(TRIP_PAYMENT_MODE.UNKNOWN, '', false);
}

function build(mode, detail, isAccountBilled) {
  return { mode, label: MODE_LABELS[mode], detail: detail || '', isAccountBilled };
}

export default resolveTripPaymentMode;
